import { useQuery } from '@tanstack/react-query';
import {
  CONTEXT_SECTIONS,
  CONTEXT_SECTION_TITLES,
  containsLikelySecret,
  type ContextSection,
} from '@lixionary-jira/domain';
import { BookOpen, Copy, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAssistantStatus } from '../../api/assistant';
import { api, useAction, useProject } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import { SettingsNav } from '../settings/SettingsNav';
import type {
  ContextSectionView,
  ContextVersionView,
  GeneratedContext,
  ProjectDetail,
} from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Button } from '../../ui/Button';
import { DownloadPdfButton } from '../../ui/DownloadPdf';
import { Alert, EmptyState, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Field, Input, Segmented, Textarea } from '../../ui/forms';
import { buildExternalPrompt } from '../../lib/context-prompt';
import { Markdown } from '../../ui/Markdown';
import { Modal } from '../../ui/Modal';
import { ActorName, Card, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

/** PRJ-05/06: versioned Markdown context with templates and missing-section indicators. */
export function ContextPage() {
  const { key, section } = useParams();
  const navigate = useNavigate();
  const project = useProject(key);
  const valid = (CONTEXT_SECTIONS as readonly string[]).includes(section ?? '');
  const sec = (valid ? section : 'overview') as ContextSection;
  const title = CONTEXT_SECTION_TITLES[sec];
  usePageTitle(project.data ? `${project.data.name} · ${title}` : null);
  useCrumbs([
    { label: project.data?.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Settings', to: `/projects/${key}/settings` },
    { label: 'Context', to: `/projects/${key}/settings/context/overview` },
    { label: title },
  ]);
  const admin = useIsAdmin();
  const q = useQuery({
    queryKey: ['context', key, sec],
    queryFn: () => api.get<ContextSectionView>(`/projects/${key}/context/${sec}`),
    enabled: valid,
  });
  const [editing, setEditing] = useState(false);
  if (!valid) return <PageUnavailable />;
  const canEdit = admin && project.data && !project.data.archived;
  return (
    <div className="page">
      <PageHeader
        title="Project context"
        subtitle="Project context is versioned. Implementations and releases record the versions they used."
      />
      <SettingsNav projectKey={key!} />
      <div className="row" style={{ marginTop: 16 }}>
        <Segmented
          label="Context section"
          value={sec}
          onChange={(s) => {
            setEditing(false);
            navigate(`/projects/${key}/settings/context/${s}`);
          }}
          options={CONTEXT_SECTIONS.map((s) => ({
            value: s,
            label: CONTEXT_SECTION_TITLES[s],
          }))}
        />
      </div>
      <QueryView query={q}>
        {(ctx) => (
          <div className="split">
            <div className="stack">
              {ctx.missing.length > 0 && !editing && (
                <Alert tone="warning" title={ctx.current ? 'Incomplete section' : 'Missing section'}>
                  <span>Add content under: {ctx.missing.join(', ')}.</span>
                </Alert>
              )}
              {editing && project.data ? (
                <ContextEditor project={project.data} ctx={ctx} onDone={() => setEditing(false)} />
              ) : ctx.current ? (
                <Card
                  title={`Version ${ctx.current.number}`}
                  subtitle={
                    <>
                      <ActorName actor={ctx.current.author} /> · <Timestamp value={ctx.current.createdAt} /> ·{' '}
                      {ctx.current.changeSummary}
                    </>
                  }
                  action={
                    <div className="row">
                      <ContextPdfButton
                        projectName={project.data?.name ?? key!}
                        sectionTitle={title}
                        version={ctx.current}
                      />
                      {canEdit && (
                        <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                          Edit
                        </Button>
                      )}
                    </div>
                  }
                >
                  <Markdown>{ctx.current.body}</Markdown>
                </Card>
              ) : (
                <Card>
                  <EmptyState
                    icon={BookOpen}
                    title={`No ${title.toLowerCase()} yet`}
                    action={canEdit && <Button onClick={() => setEditing(true)}>Start from template</Button>}
                  >
                    {canEdit
                      ? 'Start from the template; each heading is part of the completeness check.'
                      : 'An admin has not written this section yet.'}
                  </EmptyState>
                </Card>
              )}
            </div>
            <Card title="History" tight>
              {ctx.history.length === 0 ? (
                <span className="small muted">No versions yet.</span>
              ) : (
                <ol className="checklist">
                  {ctx.history.map((h) => (
                    <li key={h.id} style={{ flexDirection: 'column', gap: 2 }}>
                      <Link
                        to={`/projects/${key}/settings/context/${sec}/versions/${h.number}`}
                        className="mono"
                      >
                        v{h.number}
                      </Link>
                      <span className="small">{h.changeSummary}</span>
                      <span className="small muted">
                        <ActorName actor={h.author} /> · <Timestamp value={h.createdAt} />
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </div>
        )}
      </QueryView>
    </div>
  );
}

/** Saves one context version as an A4 PDF, headed with the project, section and version details. */
export function ContextPdfButton({
  projectName,
  sectionTitle,
  version,
}: {
  projectName: string;
  sectionTitle: string;
  version: ContextVersionView;
}) {
  return (
    <DownloadPdfButton
      fileName={`${projectName} - ${sectionTitle} v${version.number}`}
      title={`${projectName} · ${sectionTitle}`}
      details={
        <>
          Version {version.number} · <ActorName actor={version.author} /> ·{' '}
          <Timestamp value={version.createdAt} /> · {version.changeSummary}
        </>
      }
      body={version.body}
    />
  );
}

export function ContextEditor({
  project,
  ctx,
  onDone,
}: {
  project: ProjectDetail;
  ctx: ContextSectionView;
  onDone: () => void;
}) {
  const projectKey = project.key;
  const seed = ctx.current?.body ?? ctx.template;
  const [body, setBody] = useState(seed);
  const [summary, setSummary] = useState('');
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const [generated, setGenerated] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const status = useAssistantStatus();
  const save = useAction(
    () =>
      api.post(`/projects/${projectKey}/context/${ctx.section}`, {
        body,
        changeSummary: summary,
        expectedCurrentVersionId: ctx.current?.id ?? null,
      }),
    {
      success: 'Saved new version',
      invalidate: [
        ['context', projectKey],
        ['project', projectKey],
      ],
      onSuccess: onDone,
    },
  );
  const generate = useAction(
    () => api.post<GeneratedContext>(`/projects/${projectKey}/context/${ctx.section}/generate`),
    {
      success: 'Draft ready. Review it before you save a version.',
      onSuccess: (r) => {
        setBody(r.body);
        setGenerated(r.body);
        setMode('write');
      },
    },
  );
  // Generating replaces the buffer, so it is offered only while the admin has not changed it.
  const pristine = body === seed || body === generated;
  const unavailable = status.data && !status.data.available;
  const generateHint = unavailable
    ? (status.data?.reason ?? 'The ticket assistant is not available right now.')
    : !pristine
      ? 'You have unsaved edits. Generating would replace them.'
      : 'Drafts this section with the ticket assistant. Each draft uses model API tokens.';
  return (
    <Card
      title={`Edit ${ctx.title}`}
      subtitle="Saving creates a new immutable version. Never paste secret values; reference where they live instead."
    >
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Segmented
          label="Editor mode"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'write', label: 'Write' },
            { value: 'preview', label: 'Preview' },
          ]}
        />
        <div className="row">
          {(!ctx.current || ctx.missing.length > 0) && (
            <span title={generateHint}>
              <Button
                variant="secondary"
                size="sm"
                icon={<Sparkles size={14} />}
                onClick={() => generate.mutate(undefined)}
                loading={generate.isPending}
                disabled={!status.data?.available || !pristine}
                aria-description={generateHint}
              >
                {generate.isPending ? 'Generating…' : 'Generate with assistant'}
              </Button>
            </span>
          )}
          <Button variant="secondary" size="sm" icon={<Copy size={14} />} onClick={() => setShowPrompt(true)}>
            Copy prompt for your AI
          </Button>
        </div>
      </div>
      <ErrorAlert error={generate.error} />
      {mode === 'write' ? (
        <Field label="Markdown">
          {(p) => (
            <Textarea
              {...p}
              mono
              value={body}
              onChange={(e) => setBody(e.target.value)}
              readOnly={generate.isPending}
              rows={22}
            />
          )}
        </Field>
      ) : (
        <div className="well">
          <Markdown>{body}</Markdown>
        </div>
      )}
      <Field label="Change summary" hint="Why this version exists. Shown in history and audit.">
        {(p) => (
          <Input
            {...p}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="e.g. Clarify offline guarantees"
            maxLength={500}
          />
        )}
      </Field>
      {ctx.section === 'deployment' && (
        <Alert tone="info">
          Saving deployment instructions invalidates outstanding release approvals in this project.
        </Alert>
      )}
      {containsLikelySecret(body) && (
        <Alert tone="warning" title="This looks like a secret value">
          Context must say where a password, token or private key lives, never contain it. Remove it before
          you save; the version will be refused otherwise.
        </Alert>
      )}
      <ErrorAlert error={save.error} />
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button
          onClick={() => save.mutate(undefined)}
          loading={save.isPending}
          disabled={!summary.trim() || generate.isPending}
        >
          Save version
        </Button>
      </div>
      {showPrompt && (
        <ExternalPromptDialog
          prompt={buildExternalPrompt({
            project,
            repository: project.setup.repository,
            section: ctx.section,
            currentBody: ctx.current?.body ?? null,
          })}
          onClose={() => setShowPrompt(false)}
        />
      )}
    </Card>
  );
}

/** The external-AI prompt, copied like the commands in AgentSetupGuide. */
function ExternalPromptDialog({ prompt, onClose }: { prompt: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    navigator.clipboard.writeText(prompt).then(
      () => setCopied(true),
      () => undefined,
    );
  return (
    <Modal
      title="Copy prompt for your AI"
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button icon={<Copy size={14} />} onClick={copy} data-autofocus>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </>
      }
    >
      <p className="small muted">
        Run this prompt in Claude Code, Cursor or another AI agent, ideally in a checkout of the repository.
        Paste its answer into the Markdown field and click Save version.
      </p>
      <pre
        className="mono small"
        role="region"
        aria-label="Prompt"
        tabIndex={0}
        style={{ whiteSpace: 'pre-wrap', margin: 0, maxHeight: 420, overflow: 'auto' }}
      >
        {prompt}
      </pre>
    </Modal>
  );
}
